export interface TerminalMessage<T extends string, P = undefined> {
    type: T;
    payload: P;
}

export interface InitPayload {
    companyId: number;
    /**
     * The integration application name (e.g. `'toast'`, `'square'`). Required
     * so the terminal can scope mapping lookups to the correct integration.
     */
    applicationName: string;
    refreshToken?: string;
    theme?: 'light' | 'dark';
    /** BCP-47 language tag for the active user locale (e.g. `'en'`, `'fr-CA'`). */
    lang?: string;
    /**
     * External user ID from the partner app. When provided, the terminal skips
     * PIN entry and automatically resolves this ID to the employee's PIN,
     * landing directly on the user-status screen.
     */
    externalUserId?: string;
}

export type InitMessage = TerminalMessage<'7punches:init', InitPayload>;

export type DestroyMessage = TerminalMessage<'7punches:destroy'>;

export interface SetExternalUserPayload {
    externalUserId: string;
}

/**
 * Sent by the host to (re)set the active external user without re-initialising
 * the terminal. Ignored if the terminal is not in the idle phase.
 */
export type SetExternalUserMessage = TerminalMessage<
    '7punches:set_external_user',
    SetExternalUserPayload
>;

export type InboundMessage =
    | InitMessage
    | DestroyMessage
    | SetExternalUserMessage;

export type ReadyMessage = TerminalMessage<'7punches:ready'>;

/**
 * Sends the current refresh token to the host for persistent storage.
 * Emitted after the initial OAuth login and after every token refresh so
 * the host can pass it back via `7punches:init` on the next launch.
 */
export interface SessionTokenPayload {
    refreshToken: string;
}

export type SessionTokenMessage = TerminalMessage<
    '7punches:session_token',
    SessionTokenPayload
>;

/** Sent when the terminal cannot recover an expired session on its own. */
export type SessionExpiredMessage = TerminalMessage<'7punches:session_expired'>;

/**
 * The always-available "thin" shape — client-observed timing only, no
 * authoritative server data. Used for break punches (enrichment deferred,
 * decision 2026-07-27) and as the fallback for clock_in/clock_out when
 * enrichment fails, times out, or the terminal is offline.
 */
export interface ThinPunchSuccessPayload {
    punchType: 'clock_in' | 'clock_out' | 'break_start' | 'break_end';
    punchId: number;
    punchedAt: string;
    userId: number;
}

export interface EnrichedPunchSuccessBreak {
    breakId: number;
    in: string;
    out: string | null;
    paid: boolean;
}

/**
 * The curated, fully-resolved shape — server-authoritative times plus both
 * partner external IDs, resolved via the punch enrichment endpoint
 * (API-4675). First release covers `clock_in`/`clock_out` only; break punches
 * stay on `ThinPunchSuccessPayload` (decision 2026-07-27, see
 * postmessage-protocol-reference.md). Unmapped external IDs are `null` — the
 * key is always present, never omitted.
 */
export interface EnrichedPunchSuccessPayload {
    punchType: 'clock_in' | 'clock_out';
    punchId: number;
    userId: number;
    externalUserId: string | null;
    clockedIn: string;
    clockedOut: string | null;
    roleId: number;
    externalRoleId: string | null;
    shiftId: number;
    departmentId: number;
    locationId: number;
    tips: number | null;
    breaks: EnrichedPunchSuccessBreak[];
}

/**
 * Offline punches never reach KMP or the API (`PunchFlowService.start()` is
 * never called while offline — see `PunchFlowContext.handlePin`), so this can
 * only reflect what is queued locally (`OfflinePunch` in `offlinePunchQueue.ts`).
 *
 * `offline: true` marks this event as **unconfirmed** — the punch is recorded
 * in the local queue, not yet accepted by the server. This deliberately
 * loosens the usual meaning of `punch_success` ("the API accepted this
 * punch"); the host must treat `offline: true` as pending, not final.
 *
 * Deliberately absent (unlike `ThinPunchSuccessPayload`/`EnrichedPunchSuccessPayload`):
 * - `punchType` / `punchId` — direction (in/out/break) and the authoritative
 *   `time_punch.id` are both resolved server-side at sync, not on-device.
 * - `userId` / `externalUserId` — offline we only have the raw PIN, never a
 *   resolved user identity, and we deliberately store no PIN→user mapping
 *   (see `CONTRIBUTING.md` → "Offline Data & External ID Mappings").
 *
 * `externalRoleId` mirrors `EnrichedPunchSuccessPayload`'s convention: the key
 * is always present, `null` when no role was selected or the role is
 * unmapped/uncached (resolved from the `role_mapping_cache`, API-4674).
 */
export interface OfflinePunchSuccessPayload {
    offline: true;
    time: string; // ISO 8601 UTC — DEVICE clock at punch, not server-authoritative
    companyId: number;
    locationHash: string; // offline queue keys on hash, not locationId
    roleId: number | null;
    externalRoleId: string | null;
    departmentId: number | null;
    tips: null; // tips are never captured offline
}

export type PunchSuccessPayload =
    | ThinPunchSuccessPayload
    | EnrichedPunchSuccessPayload
    | OfflinePunchSuccessPayload;

export type PunchSuccessMessage = TerminalMessage<
    '7punches:punch_success',
    PunchSuccessPayload
>;

export interface PunchErrorPayload {
    category: 'network' | 'auth' | 'validation' | 'server' | 'unknown';
    message: string;
}

export type PunchErrorMessage = TerminalMessage<
    '7punches:punch_error',
    PunchErrorPayload
>;

/**
 * Sent when the terminal has finished its work and the host should close the
 * webview. Emitted instead of returning to the PIN pad when the terminal is
 * operating in external-user mode (i.e. `externalUserId` was provided at init).
 */
export type ExitMessage = TerminalMessage<'7punches:exit'>;

export type OutboundMessage =
    | ReadyMessage
    | SessionTokenMessage
    | SessionExpiredMessage
    | PunchSuccessMessage
    | PunchErrorMessage
    | ExitMessage;

/**
 * Sent from the OAuth popup back to the admin-panel opener page after login.
 * This is popup→opener traffic only — it is intentionally excluded from
 * OutboundMessage (iframe traffic) and from INBOUND_TYPES (the terminal
 * never receives it).
 */
export interface HarnessTokenPayload {
    refreshToken: string;
}

export type HarnessTokenMessage = TerminalMessage<
    '7punches:harness_token',
    HarnessTokenPayload
>;

const INBOUND_TYPES = new Set<string>([
    '7punches:init',
    '7punches:destroy',
    '7punches:set_external_user'
]);

export function isInboundMessage(data: unknown): data is InboundMessage {
    if (typeof data !== 'object' || data === null) return false;
    const record = data as Record<string, unknown>;
    if (typeof record.type !== 'string' || !INBOUND_TYPES.has(record.type))
        return false;

    // destroy has no payload (payload is undefined)
    if (record.type === '7punches:destroy') return true;

    // set_external_user requires a payload with a non-empty externalUserId string
    if (record.type === '7punches:set_external_user') {
        if (typeof record.payload !== 'object' || record.payload === null)
            return false;
        const p = record.payload as Record<string, unknown>;
        return typeof p.externalUserId === 'string' && p.externalUserId.length > 0;
    }

    // all other inbound messages require a payload object
    return typeof record.payload === 'object' && record.payload !== null;
}

export function isInitPayload(payload: unknown): payload is InitPayload {
    if (typeof payload !== 'object' || payload === null) return false;
    const p = payload as Record<string, unknown>;
    return (
        typeof p.companyId === 'number' &&
        typeof p.applicationName === 'string' &&
        p.applicationName.length > 0
    );
}

export function sanitizeInitPayload(payload: InitPayload): InitPayload {
    const sanitized: InitPayload = {
        companyId: payload.companyId,
        applicationName: payload.applicationName,
    };
    if (
        typeof payload.refreshToken === 'string' &&
        payload.refreshToken.length > 0
    ) {
        sanitized.refreshToken = payload.refreshToken;
    }
    if (payload.theme === 'light' || payload.theme === 'dark') {
        sanitized.theme = payload.theme;
    }
    if (typeof payload.lang === 'string' && payload.lang.length > 0) {
        sanitized.lang = payload.lang;
    }
    if (
        typeof payload.externalUserId === 'string' &&
        payload.externalUserId.length > 0
    ) {
        sanitized.externalUserId = payload.externalUserId;
    }
    return sanitized;
}
